import React, { useEffect, useLayoutEffect, useState } from "react";
import cx from "classnames";
import { DropdownItem, DropdownMenu, DropdownToggle, Input, UncontrolledDropdown } from "reactstrap";

import global from "../../scss/dhp.scss";
import Calendar from "./Calendar";
import { Icon } from "./icon";
import { dateConverter } from "../../_helpers/utils";

let style = Object.assign({}, global);

const DateTimeField = ({ updateState, maxLength, ...props }) => {
  // Add time state management
  const [calendarFieldValue, setCalendarFieldValue] = useState();
  const [timeValue, setTimeValue] = useState("12:00");
  const [ampm, setAmpm] = useState("AM");
  const [timeError, setTimeError] = useState("");
  const [firstValueLoad, setFirstValueLoad] = useState(false);
  const [defaultValue, setDefaultValue] = useState("");

  let format = `${props.dateFormat || "DD/MM/YYYY"} hh:mm A`;

  // Time validation function
  const validateTime = value => {
    // Allow empty value
    if (value === "") {
      setTimeError("");
      return true;
    }

    // Basic format validation
    const timeRegex = /^([0-9]{0,2}):?([0-9]{0,2})$/;
    const match = value.match(timeRegex);

    if (!match) {
      setTimeError("Invalid time format");
      return false;
    }

    let hours = match[1];
    let minutes = match[2];

    // Auto-insert colon after 2 digits
    if (hours.length === 2 && minutes.length === 0 && !value.includes(":")) {
      hours = hours.substring(0, 2);
      return `${hours}:`;
    }

    // Validate hours (01-12)
    if (hours && hours.length === 2) {
      const hourNum = parseInt(hours, 10);
      if (hourNum < 1 || hourNum > 12) {
        setTimeError("Hour must be between 01-12");
        return false;
      }
    }

    // Validate minutes (00-59)
    if (minutes && minutes.length === 2) {
      const minuteNum = parseInt(minutes, 10);
      if (minuteNum < 0 || minuteNum > 59) {
        setTimeError("Minute must be between 00-59");
        return false;
      }
    }

    setTimeError("");
    return true;
  };

  // Handle time input change
  const handleTimeChange = e => {
    const value = e.target.value;

    // Remove any non-digit or colon characters
    let cleanValue = value.replace(/[^\d:]/g, "");

    // Ensure only one colon
    const colonCount = (cleanValue.match(/:/g) || []).length;
    if (colonCount > 1) {
      cleanValue = cleanValue.replace(/:/g, "").substring(0, 4);
      cleanValue = cleanValue.substring(0, 2) + ":" + cleanValue.substring(2);
    }

    // Limit to 5 characters (HH:MM)
    if (cleanValue.length > 5) {
      cleanValue = cleanValue.substring(0, 5);
    }

    // Auto-format as user types
    if (cleanValue.length === 2 && !cleanValue.includes(":")) {
      const hours = cleanValue.substring(0, 2);
      const hourNum = parseInt(hours, 10);

      // Validate hours before auto-adding colon
      if (hourNum >= 1 && hourNum <= 12) {
        cleanValue = hours + ":";
      }
    }

    // Validate the formatted value
    if (validateTime(cleanValue)) {
      setTimeValue(cleanValue);
      // Call updateState if needed with the full datetime
      // updateState({ time: cleanValue, ampm });
    }
  };

  // Handle blur to ensure complete format
  const handleTimeBlur = () => {
    if (!timeValue) return;

    const parts = timeValue.split(":");
    let hours = parts[0] || "";
    let minutes = parts[1] || "";

    // Pad hours to 2 digits if needed
    if (hours.length === 1 && hours !== "") {
      hours = "0" + hours;
    }

    // Pad minutes to 2 digits if needed
    if (minutes.length === 1 && minutes !== "") {
      minutes = "0" + minutes;
    } else if (minutes === "" && hours) {
      minutes = "00";
    }

    if (hours && minutes) {
      const formattedTime = `${hours}:${minutes}`;
      if (validateTime(formattedTime)) {
        setTimeValue(formattedTime);
      }
    }
  };

  const formatDateTime = (convertDirection = "toUnix") => {
    let format = `${props.dateFormat || "DD/MM/YYYY"} hh:mm A`;
    let inputValue = `${calendarFieldValue} ${timeValue || "12:00"} ${ampm}`;
    let calculatedDate = dateConverter(inputValue, format, convertDirection);
    return calculatedDate;
  };

  const changeDateFn = value => {
    setCalendarFieldValue(value);
  };

  // Handle AM/PM selection
  const handleAmPmChange = selectedAmpm => {
    setAmpm(selectedAmpm);
  };

  useEffect(() => {
    let calculatedDate = formatDateTime();
    updateState(calculatedDate);
  }, [calendarFieldValue, timeValue, ampm]);

  useLayoutEffect(() => {
    if (props?.value && !firstValueLoad) {
      setFirstValueLoad(true);
      let calculatedDate = dateConverter(props.value, format, "toString");
      let dateTimeArray = calculatedDate.split(" ");
      if (dateTimeArray?.[0]) {
        setDefaultValue(dateTimeArray[0]);
        setCalendarFieldValue(dateTimeArray[0]);
      }
      if (dateTimeArray?.[1]) setTimeValue(dateTimeArray[1]);
      if (dateTimeArray?.[2]) setAmpm(dateTimeArray[2]);
    }
  }, [props.value]);

  return (
    <div className={cx(style["d-flex"], style["dateTime-field"])}>
      <div className={cx(style["custom-date-picker"], style["w-65"])}>
        <Calendar updateState={changeDateFn} cssModule={style} {...props} defaultValue={defaultValue} />
      </div>
      <div className={cx(style["d-flex"], style["time-section"], style["w-35"])}>
        <Input
          autoComplete="off"
          type={"text"}
          maxLength={maxLength ?? 5}
          placeholder="HH:MM"
          onChange={handleTimeChange}
          onBlur={handleTimeBlur}
          value={timeValue}
        />
        {/* <span className="time-value">{"10:40"}</span> */}
        <UncontrolledDropdown>
          <DropdownToggle tag={"a"} caret>
            {ampm}
            <Icon icon="ui-arrow-down" additionalclass={"ms-2"} />
          </DropdownToggle>
          <DropdownMenu right className={cx(style["shadow"], style["border-0"])}>
            <DropdownItem tag={"a"} onClick={() => handleAmPmChange("AM")}>
              AM
            </DropdownItem>
            <DropdownItem tag={"a"} onClick={() => handleAmPmChange("PM")}>
              PM
            </DropdownItem>
          </DropdownMenu>
        </UncontrolledDropdown>
      </div>
    </div>
  );
};

export default DateTimeField;
